import { ArrowLeft, Info, Plus } from 'lucide-react';
import { Badge, WidgetCardShell, WidgetGridProvider } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import WIDGET_PREVIEW_DATA from '../constants/widgetPreviewData';
import DashboardButton from './DashboardButton';
import { renderWidgetLibraryPreviewBody } from './widgetLibraryPreviewBody';

/** Max width per preview size only — card height follows content (no forced min-height). */
const DETAIL_PREVIEW_MAX_WIDTH_BY_SIZE = Object.freeze({
  small: 'w-full max-w-[360px]',
  medium: 'w-full max-w-[720px]',
  large: 'w-full max-w-[920px]',
});

const renderDetailWidgetPreview = (widget, size) => {
  const previewData = WIDGET_PREVIEW_DATA[widget?.type] ?? null;
  return renderWidgetLibraryPreviewBody(widget, previewData, size === 'small');
};

const WidgetLibraryDetailView = ({
  widget,
  previewSize,
  previewSizes,
  onBack,
  onPreviewSizeChange,
}) => {
  const { t } = useTranslation('dashboards');
  return (
    <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto pr-1">
    <div className="mb-4 flex items-center gap-2">
      <DashboardButton
        variant="ghost"
        size="sm"
        leftIcon={ArrowLeft}
        className="!h-7 !w-7 !p-0 text-content-muted hover:text-content"
        onClick={onBack}
        aria-label={t('widgetLibrary.detail.backAriaLabel')}
      />
      <h3 className="m-0 text-lg font-semibold text-content">{widget.name}</h3>
    </div>

    <div className="mb-3 rounded-md border border-edge bg-surface px-4 py-3">
      <p className="m-0 text-sm font-semibold text-content">{t('widgetLibrary.detail.aboutTitle')}</p>
      <p className="m-0 mt-1 text-sm text-content-secondary">{widget.description}</p>
    </div>

    {/* Inline callout: same info layout as toast `info` variant, without portal — fills width, no border */}
    <div className="relative mb-3 flex items-start gap-3 rounded-lg bg-blue-50 px-4 py-3.5 dark:bg-blue-950/30">
      <Info
        size={20}
        strokeWidth={2}
        className="mt-0.5 shrink-0 text-content-secondary"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="m-0 text-sm font-semibold text-content">{t('widgetLibrary.detail.bestForTitle')}</p>
        <p className="m-0 mt-1 text-sm leading-snug text-content-secondary">
          {widget.bestFor || widget.description}
        </p>
      </div>
    </div>

    <div className="flex min-h-[min(52vh,560px)] flex-col rounded-lg bg-brand-50/55 p-5 dark:bg-brand-950/25">
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <p className="m-0 text-sm font-semibold text-content">{t('widgetLibrary.detail.previewTitle')}</p>
        <div className="inline-flex shrink-0 flex-wrap items-center justify-end gap-1">
          {previewSizes.map((size) => {
            const active = previewSize === size;
            const label = size[0].toUpperCase() + size.slice(1);
            return (
              <Badge
                key={size}
                variant={active ? 'primary' : 'secondary'}
                size="medium"
                clickable
                onClick={() => onPreviewSizeChange(size)}
              >
                {label}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col items-center justify-start pb-6 pt-1">
        <div
          className={[
            'mx-auto h-fit w-full overflow-hidden shadow-[0_2px_10px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.35)]',
            DETAIL_PREVIEW_MAX_WIDTH_BY_SIZE[previewSize] || DETAIL_PREVIEW_MAX_WIDTH_BY_SIZE.medium,
          ].join(' ')}
        >
          <WidgetGridProvider isEditMode>
            <WidgetCardShell
              title={widget?.name ?? ''}
              headerButtons={[
                {
                  key: 'add',
                  icon: Plus,
                  tooltip: t('widgetLibrary.detail.previewAddTooltip'),
                  action: {},
                },
              ]}
              isPinned={false}
              onPin={() => {}}
              onDelete={() => {}}
              onAction={() => {}}
            >
              {renderDetailWidgetPreview(widget, previewSize)}
            </WidgetCardShell>
          </WidgetGridProvider>
        </div>
      </div>
    </div>
  </div>
  );
};

export default WidgetLibraryDetailView;
